import { dispatch, select } from '@pressreader/appstore';
import { HttpError } from '@pressreader/types';
import { newGuid } from '@pressreader/utils';

import { resetCheckoutAttempt, setCheckoutIdempotencyKey } from './actions';
import { selectIdempotencyKey } from './selectors';

/**
 * The `Idempotency-Key` of the current checkout attempt, starting an attempt if none is active.
 *
 * An attempt is started lazily, where the key is used, so a caller never has to know what came
 * before it: the key survives until something invalidates it — `resetCheckoutAttempt`, a user
 * change, a page reload — and the next purchase starts a fresh one.
 *
 * The return type is deliberately not optional. An absent key is sent as no `Idempotency-Key`
 * header at all (see `withIdempotencyKey`), which leaves the request with no protection against
 * creating a second order, so there is no caller for whom "no key" is a valid state.
 */
export function getOrStartCheckoutAttemptKey(): string {
    const currentKey = select(selectIdempotencyKey);
    if (currentKey) {
        return currentKey;
    }

    const key = newGuid();
    dispatch(setCheckoutIdempotencyKey({ key }));

    return key;
}

/**
 * End the attempt the given key belongs to, if it is still the attempt in progress.
 *
 * Ending is expressed against a key rather than unconditionally because the caller reporting the
 * end is not always the current one: a payment session can report its outcome long after it was
 * abandoned, by which time the buyer may have started another attempt. Clearing the key blindly
 * would take that newer attempt's key away mid-flight, and its next request would create a second
 * order instead of retrieving its own.
 */
export function endCheckoutAttempt(key: string): void {
    if (select(selectIdempotencyKey) !== key) {
        return;
    }

    dispatch(resetCheckoutAttempt());
}

/**
 * Whether a failed request still said what became of the order it was sent about.
 *
 * Normally it did not — the order may have been created and only this key retrieves it. A key
 * conflict is the exception: the key is held by a request with a different body, so this attempt
 * can never succeed under it and every repeat would be refused the same way. A response, whatever
 * its status, always says so: the order is then either finished or abandoned.
 */
function failureStatesTheOutcome(err: unknown): boolean {
    return err instanceof HttpError && err.status === 409;
}

/**
 * One checkout attempt, handed to the flow that runs it.
 *
 * Payment methods differ in how a purchase is approved; they do not differ in what an attempt is.
 * A flow takes the key from here, declares every request that creates or completes an order to
 * `send`, and never decides the attempt's fate itself.
 */
export interface CheckoutAttempt {
    /** The `Idempotency-Key` every request of this attempt must carry. */
    readonly key: string;
    /** Declare a request whose failure would leave the state of the order unknown. */
    send<T>(request: Promise<T>): Promise<T>;
}

/**
 * Run a purchase flow as one checkout attempt, and decide the attempt's fate from what its requests
 * answered.
 *
 * The rule is: **end the attempt unless a request it sent left the order's state unknown.** An
 * attempt exists to let a repeat retrieve an order that may already have been created instead of
 * creating a second one, and that is needed in exactly one situation — a request went out and its
 * answer never came back. A known order is either finished or abandoned, and carrying an abandoned
 * one into the next click is what makes a purchase unreachable: it has no approval handle left, so
 * every further click is answered with it until the page is reloaded.
 *
 * The rule is applied to the requests themselves rather than at each of the flow's exits, because
 * enumerating exits is what lets one be forgotten. It reads every request the flow declared, so a
 * failure the flow caught and continued past still counts, and one still in flight when the flow
 * gives up is simply awaited — a blocked popup fails long before the checkout replies, and the
 * answer that decides may arrive after the buyer has been told. Ending is keyed on this attempt, so
 * a late decision cannot touch one the buyer has started since.
 */
function startAttempt(): { attempt: CheckoutAttempt; finish: () => void } {
    const key = getOrStartCheckoutAttemptKey();
    const requests: Promise<unknown>[] = [];

    const attempt: CheckoutAttempt = {
        key,
        send<R>(request: Promise<R>): Promise<R> {
            requests.push(request);

            return request;
        },
    };

    const decide = async (): Promise<void> => {
        const known = await Promise.all(requests.map(request => request.then(() => true, failureStatesTheOutcome)));

        if (known.every(Boolean)) {
            endCheckoutAttempt(key);
        }
    };

    // The flow does not wait for the decision: what it returns is the purchase's outcome, and the
    // key's fate is settled once the last request has spoken, which can be later.
    return { attempt, finish: (): void => void decide() };
}

export async function runCheckoutAttempt<T>(flow: (attempt: CheckoutAttempt) => Promise<T>): Promise<T> {
    const { attempt, finish } = startAttempt();

    try {
        return await flow(attempt);
    } finally {
        finish();
    }
}

/**
 * The same rule for a flow written as a saga.
 *
 * Purchase flows that need saga effects between their requests — a confirmation prompt while a 3DS
 * window is open, a navigation — cannot be expressed as a promise without losing cancellation. They
 * get the identical rule from the identical implementation; only the shape of the flow differs.
 * Cancelling the saga finishes the attempt too, and by the same rule: a request left in flight
 * keeps the key, because the order it was creating may exist.
 */
export function* runCheckoutAttemptSaga<T>(flow: (attempt: CheckoutAttempt) => Generator<unknown, T>): Generator<unknown, T> {
    const { attempt, finish } = startAttempt();

    try {
        return yield* flow(attempt);
    } finally {
        finish();
    }
}
